/**
 * KatalogRightPanelSlot
 * Self-contained component rendered by DashboardLayout when on /katalog route.
 * Reads URL state directly, renders filter/search panel content.
 *
 * Uses useIndustryCategoryFilter to ensure categories are filtered
 * by selected industries and invalid selections are auto-pruned.
 *
 * Supports: q, sections, industry_ids, category_ids, focus
 */
import React, { useMemo, useCallback, useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { RightPanelMode } from '@bright-web-skeleton/ui';
import type { KatalogSectionKey } from './KatalogShortcutRail';
import { KatalogFilterPanelContent, type KatalogFocus } from './KatalogFilterPanelContent';
import { KatalogSearchPanelContent } from './KatalogSearchPanelContent';
import { supabase } from '../../lib/supabase';
import {
  getAllIndustries,
  type NormalizedIndustryOption,
} from '@bright-web-skeleton/discover-contract';
import { useIndustryCategoryFilter, type CategoryOption } from '@bright-web-skeleton/ui';
import { useDatasetFacets } from '../../lib/DatasetFacetsContext';

interface KatalogRightPanelSlotProps {
  panel: RightPanelMode;
}

const VALID_SECTIONS = new Set<KatalogSectionKey>(['active', 'paused', 'ended']);
const VALID_FOCUSES = new Set<KatalogFocus>(['katalog', 'events', 'portfolio']);

function parseSections(params: URLSearchParams): KatalogSectionKey[] {
  return params.getAll('sections').filter((s): s is KatalogSectionKey => VALID_SECTIONS.has(s as KatalogSectionKey));
}

function parseFocus(params: URLSearchParams): KatalogFocus {
  const raw = params.get('focus') || 'katalog';
  return VALID_FOCUSES.has(raw as KatalogFocus) ? (raw as KatalogFocus) : 'katalog';
}

function buildKatalogUrl(params: {
  q?: string;
  sections?: KatalogSectionKey[];
  deal_type?: string;
  industry_ids?: string[];
  category_ids?: string[];
  focus?: KatalogFocus;
}): URLSearchParams {
  const urlParams = new URLSearchParams();
  if (params.q?.trim()) urlParams.set('q', params.q.trim());
  params.sections?.forEach((s) => urlParams.append('sections', s));
  if (params.deal_type && params.deal_type !== 'all') urlParams.set('deal_type', params.deal_type);
  params.industry_ids?.forEach((id) => urlParams.append('industry_ids', id));
  params.category_ids?.forEach((id) => urlParams.append('category_ids', id));
  if (params.focus && params.focus !== 'katalog') urlParams.set('focus', params.focus);
  return urlParams;
}

export const KatalogRightPanelSlot: React.FC<KatalogRightPanelSlotProps> = ({ panel }) => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Read committed state from URL
  const committedQuery = searchParams.get('q') || '';
  const committedSections = useMemo(() => parseSections(searchParams), [searchParams]);
  const committedDealType = searchParams.get('deal_type') || 'all';
  const committedIndustryIds = useMemo(() => searchParams.getAll('industry_ids'), [searchParams]);
  const committedCategoryIds = useMemo(() => searchParams.getAll('category_ids'), [searchParams]);
  const committedFocus = useMemo(() => parseFocus(searchParams), [searchParams]);

  // Draft state for filter panel
  const [draftQuery, setDraftQuery] = useState(committedQuery);
  const [draftSections, setDraftSections] = useState<KatalogSectionKey[]>(committedSections);
  const [draftDealType, setDraftDealType] = useState(committedDealType);
  const [draftIndustryIds, setDraftIndustryIds] = useState<string[]>(committedIndustryIds);
  const [draftCategoryIds, setDraftCategoryIds] = useState<string[]>(committedCategoryIds);
  const [draftFocus, setDraftFocus] = useState<KatalogFocus>(committedFocus);

  // Taxonomy data
  const [allIndustries, setAllIndustries] = useState<NormalizedIndustryOption[]>([]);
  const [allCategories, setAllCategories] = useState<CategoryOption[]>([]);

  // Fetch taxonomy options on mount
  useEffect(() => {
    getAllIndustries(supabase).then(setAllIndustries);
    supabase
      .from('taxonomy_categories')
      .select('id, label, industry_id')
      .order('label')
      .then(({ data }) => {
        setAllCategories(
          (data || []).map((c: any) => ({
            id: c.id,
            label: c.label,
            industry_id: c.industry_id,
          })),
        );
      });
  }, []);

  // Scope options to dataset
  const { availableIndustryIds, availableCategoryIds } = useDatasetFacets();
  const scopedIndustries = useMemo(
    () => availableIndustryIds.size > 0 ? allIndustries.filter((i) => availableIndustryIds.has(i.id)) : allIndustries,
    [allIndustries, availableIndustryIds],
  );
  const scopedCategories = useMemo(
    () => availableCategoryIds.size > 0 ? allCategories.filter((c) => availableCategoryIds.has(c.id)) : allCategories,
    [allCategories, availableCategoryIds],
  );

  // ── Dependent filtering via shared hook ──
  const { filteredCategories } = useIndustryCategoryFilter({
    industries: scopedIndustries,
    categories: scopedCategories,
    selectedIndustryIds: draftIndustryIds,
    selectedCategoryIds: draftCategoryIds,
    onCategoryIdsPruned: setDraftCategoryIds,
  });

  // Sync draft with committed on URL change
  useEffect(() => { setDraftQuery(committedQuery); }, [committedQuery]);
  useEffect(() => { setDraftSections(committedSections); }, [committedSections]);
  useEffect(() => { setDraftDealType(committedDealType); }, [committedDealType]);
  useEffect(() => { setDraftIndustryIds(committedIndustryIds); }, [committedIndustryIds]);
  useEffect(() => { setDraftCategoryIds(committedCategoryIds); }, [committedCategoryIds]);
  useEffect(() => { setDraftFocus(committedFocus); }, [committedFocus]);

  // Derive CTA state
  const hasCommittedFilters = useMemo(
    () =>
      committedQuery.trim().length > 0 ||
      committedSections.length > 0 ||
      committedIndustryIds.length > 0 ||
      committedCategoryIds.length > 0 ||
      committedDealType !== 'all' ||
      committedFocus !== 'katalog',
    [committedQuery, committedSections, committedIndustryIds, committedCategoryIds, committedDealType, committedFocus],
  );

  const draftMatchesCommitted = useMemo(() => {
    if (draftQuery !== committedQuery) return false;
    if (draftSections.join(',') !== committedSections.join(',')) return false;
    if (draftDealType !== committedDealType) return false;
    if (draftIndustryIds.join(',') !== committedIndustryIds.join(',')) return false;
    if (draftCategoryIds.join(',') !== committedCategoryIds.join(',')) return false;
    if (draftFocus !== committedFocus) return false;
    return true;
  }, [draftQuery, draftSections, draftDealType, draftIndustryIds, draftCategoryIds, draftFocus, committedQuery, committedSections, committedDealType, committedIndustryIds, committedCategoryIds, committedFocus]);

  // Commit to URL
  const commitFilters = useCallback(
    (params: { q?: string; sections?: KatalogSectionKey[]; deal_type?: string; industry_ids?: string[]; category_ids?: string[]; focus?: KatalogFocus }) => {
      const merged = {
        q: params.q !== undefined ? params.q : committedQuery,
        sections: params.sections !== undefined ? params.sections : committedSections,
        deal_type: params.deal_type !== undefined ? params.deal_type : committedDealType,
        industry_ids: params.industry_ids !== undefined ? params.industry_ids : committedIndustryIds,
        category_ids: params.category_ids !== undefined ? params.category_ids : committedCategoryIds,
        focus: params.focus !== undefined ? params.focus : committedFocus,
      };
      setSearchParams(buildKatalogUrl(merged), { replace: true });
    },
    [committedQuery, committedSections, committedDealType, committedIndustryIds, committedCategoryIds, committedFocus, setSearchParams],
  );

  // Filter panel handlers
  const handleToggleDraftSection = useCallback((section: KatalogSectionKey) => {
    setDraftSections((prev) =>
      prev.includes(section) ? prev.filter((s) => s !== section) : [...prev, section],
    );
  }, []);

  const handleToggleDraftIndustry = useCallback((id: string) => {
    setDraftIndustryIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
    );
  }, []);

  const handleToggleDraftCategory = useCallback((id: string) => {
    setDraftCategoryIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
    );
  }, []);

  const handleApply = useCallback(() => {
    commitFilters({
      q: draftQuery,
      sections: draftSections,
      deal_type: draftDealType,
      industry_ids: draftIndustryIds,
      category_ids: draftCategoryIds,
      focus: draftFocus,
    });
  }, [commitFilters, draftQuery, draftSections, draftDealType, draftIndustryIds, draftCategoryIds, draftFocus]);

  const handleClear = useCallback(() => {
    setDraftQuery(committedQuery);
    setDraftSections(committedSections);
    setDraftDealType(committedDealType);
    setDraftIndustryIds(committedIndustryIds);
    setDraftCategoryIds(committedCategoryIds);
    setDraftFocus(committedFocus);
  }, [committedQuery, committedSections, committedDealType, committedIndustryIds, committedCategoryIds, committedFocus]);

  const handleClearAll = useCallback(() => {
    setDraftQuery('');
    setDraftSections([]);
    setDraftDealType('all');
    setDraftIndustryIds([]);
    setDraftCategoryIds([]);
    setDraftFocus('katalog');
    setSearchParams(new URLSearchParams(), { replace: true });
  }, [setSearchParams]);

  const handleSearchCommit = useCallback(
    (explicitQuery?: string) => {
      const q = explicitQuery ?? draftQuery;
      commitFilters({ q });
    },
    [commitFilters, draftQuery],
  );

  const handleSearchToggleSection = useCallback(
    (section: KatalogSectionKey) => {
      const newSections = committedSections.includes(section)
        ? committedSections.filter((s) => s !== section)
        : [...committedSections, section];
      commitFilters({ sections: newSections });
    },
    [committedSections, commitFilters],
  );

  if (panel === 'filters') {
    return (
      <div data-filter-panel="true" className="flex flex-col h-full min-h-0">
        <KatalogFilterPanelContent
          searchQuery={draftQuery}
          onSearchQueryChange={setDraftQuery}
          activeSections={draftSections}
          onToggleSection={handleToggleDraftSection}
          dealType={draftDealType}
          onDealTypeChange={setDraftDealType}
          focus={draftFocus}
          onFocusChange={setDraftFocus}
          industryOptions={scopedIndustries}
          activeIndustryIds={draftIndustryIds}
          onToggleIndustry={handleToggleDraftIndustry}
          categoryOptions={filteredCategories}
          activeCategoryIds={draftCategoryIds}
          onToggleCategory={handleToggleDraftCategory}
          onApply={handleApply}
          onClear={handleClear}
          onClearAll={handleClearAll}
          hasCommittedFilters={hasCommittedFilters}
          draftMatchesCommitted={draftMatchesCommitted}
        />
      </div>
    );
  }

  if (panel === 'search') {
    return (
      <div data-search-panel="true" className="flex flex-col h-full min-h-0">
        <KatalogSearchPanelContent
          searchQuery={draftQuery}
          onSearchQueryChange={setDraftQuery}
          onExecuteSearch={handleSearchCommit}
          activeSections={committedSections}
          onToggleSection={handleSearchToggleSection}
        />
      </div>
    );
  }

  return null;
};

export default KatalogRightPanelSlot;
